import { useState } from 'react'
import { AlertCircle, LineChart, Plus, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { Skeleton } from '@/components/ui/skeleton'
import { Typography } from '@/components/ui/typography'
import { SectionHeader } from '@/components/custom/companies/section-header'
import { ScorecardKpiModal } from '@/components/custom/scorecard/scorecard-kpi-modal'
import { ScorecardActualModal } from '@/components/custom/scorecard/scorecard-actual-modal'
import { ScorecardTable } from '@/components/custom/scorecard/scorecard-table'
import {
  isOgsmEnabled,
  isScorecardManualAddHidden,
} from '@/configs/feature-flags'
import {
  useCompanyScorecard,
  useDeleteScorecardKpi,
  usePortalScorecard,
  useUpdateScorecardKpi,
} from '@/hooks/use-scorecard'
import { formatDate } from '@/lib/format'
import { showSuccess } from '@/lib/toast'
import type {
  ScorecardKpiDto,
  ScorecardKpiStatus,
  ScorecardTrackedKpiDto,
} from '@/services/scorecard.service'

type ScorecardSurface = 'internal' | 'portal'

interface ScorecardTabProps {
  surface: ScorecardSurface
  companyId?: string
  fiscalYearEnd?: string | null
  canEdit?: boolean
}

function ScorecardLoadingState() {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-9 w-24" />
      </div>
      <div className="rounded-sm border border-primary/20 bg-white">
        {Array.from({ length: 5 }).map((_, index) => (
          <div
            key={index}
            className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr] gap-3 border-b border-primary/10 p-3 last:border-b-0"
          >
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-full" />
          </div>
        ))}
      </div>
    </div>
  )
}

function ScorecardErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <Empty className="min-h-72 border border-primary/15 bg-white">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <AlertCircle className="size-5 text-destructive" />
        </EmptyMedia>
        <EmptyTitle>Failed to load scorecard</EmptyTitle>
        <EmptyDescription>
          Unable to load scorecard objectives and actuals.
        </EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        <Button type="button" variant="outline" size="sm" onClick={onRetry}>
          <RefreshCw className="size-3.5" />
          Retry
        </Button>
      </EmptyContent>
    </Empty>
  )
}

function ScorecardEmptyState({
  canEdit,
  onAdd,
  surface,
}: {
  canEdit: boolean
  onAdd: () => void
  surface: ScorecardSurface
}) {
  return (
    <Empty className="min-h-72 border border-primary/15 bg-white">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <LineChart className="size-5 text-secondary" />
        </EmptyMedia>
        <EmptyTitle>No scorecard objectives yet</EmptyTitle>
        <EmptyDescription>
          {surface === 'portal'
            ? 'Greybull will share objectives here once this company scorecard is configured.'
            : 'Add an objective and goal, then enter actuals manually or link a Chronograph metric.'}
        </EmptyDescription>
      </EmptyHeader>
      {canEdit ? (
        <EmptyContent>
          <Button type="button" size="sm" onClick={onAdd}>
            <Plus className="size-3.5" />
            Add objective
          </Button>
        </EmptyContent>
      ) : null}
    </Empty>
  )
}

function getLatestAsOfDate(
  rows: Array<
    Pick<ScorecardKpiDto, 'actual'> | Pick<ScorecardTrackedKpiDto, 'actual'>
  >
) {
  const dates = rows
    .map((kpi) => kpi.actual?.asOfDate ?? kpi.actual?.periodEndDate)
    .filter((date): date is string => Boolean(date))
    .map((date) => new Date(date))
    .filter((date) => !Number.isNaN(date.getTime()))

  if (dates.length === 0) return null

  const latest = dates.reduce(
    (max, date) => (date > max ? date : max),
    dates[0]
  )
  return formatDate(latest)
}

export function ScorecardTab({
  surface,
  companyId = '',
  fiscalYearEnd,
  canEdit = false,
}: ScorecardTabProps) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingKpi, setEditingKpi] = useState<ScorecardKpiDto | null>(null)
  const [actualKpi, setActualKpi] = useState<ScorecardKpiDto | null>(null)
  const [trackedKpiForTarget, setTrackedKpiForTarget] =
    useState<ScorecardTrackedKpiDto | null>(null)

  const internalQuery = useCompanyScorecard(
    surface === 'internal' ? companyId : ''
  )
  const portalQuery = usePortalScorecard(surface === 'portal')
  const deleteKpi = useDeleteScorecardKpi(companyId)
  const updateKpi = useUpdateScorecardKpi(companyId)

  const query = surface === 'portal' ? portalQuery : internalQuery
  const kpis = query.data?.kpis ?? []
  const trackedKpis =
    surface === 'portal' ? [] : (internalQuery.data?.trackedKpis ?? [])
  const canConfigure = surface === 'internal' && canEdit && Boolean(companyId)
  const canAddObjective = canConfigure && !isScorecardManualAddHidden()
  const latestAsOfDate = getLatestAsOfDate([...kpis, ...trackedKpis])

  const openAddModal = () => {
    setEditingKpi(null)
    setTrackedKpiForTarget(null)
    setIsModalOpen(true)
  }

  const openEditModal = (kpi: ScorecardKpiDto) => {
    setEditingKpi(kpi)
    setTrackedKpiForTarget(null)
    setIsModalOpen(true)
  }

  const openAddTargetModal = (trackedKpi: ScorecardTrackedKpiDto) => {
    setEditingKpi(null)
    setTrackedKpiForTarget(trackedKpi)
    setIsModalOpen(true)
  }

  const handleDelete = async (kpiId: string) => {
    await deleteKpi.mutateAsync(kpiId)
    showSuccess('Objective removed from scorecard')
  }

  const handleStatusChange = async (
    kpi: ScorecardKpiDto,
    status: ScorecardKpiStatus | 'CALCULATED'
  ) => {
    if (status === 'CALCULATED') {
      if (kpi.statusSource === 'CALCULATED') return

      await updateKpi.mutateAsync({
        kpiId: kpi.id,
        data: { statusSource: 'CALCULATED' },
      })
      showSuccess('Objective status set to automatic')
      return
    }

    if (status === kpi.status && kpi.statusSource === 'MANUAL') return

    await updateKpi.mutateAsync({ kpiId: kpi.id, data: { status } })
    showSuccess('Objective status updated')
  }

  if (query.isLoading) {
    return <ScorecardLoadingState />
  }

  if (query.isError) {
    return <ScorecardErrorState onRetry={() => void query.refetch()} />
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Scorecard"
        afterTitle={
          <Typography variant="body-sm" className="text-primary/70">
            {latestAsOfDate ? `As of ${latestAsOfDate}` : 'Actuals vs goals'}
          </Typography>
        }
        right={
          canAddObjective ? (
            <Button type="button" size="sm" onClick={openAddModal}>
              <Plus className="size-3.5" />
              Add objective
            </Button>
          ) : null
        }
      />

      {kpis.length === 0 && trackedKpis.length === 0 ? (
        <ScorecardEmptyState
          canEdit={canAddObjective}
          onAdd={openAddModal}
          surface={surface}
        />
      ) : (
        <ScorecardTable
          kpis={kpis}
          trackedKpis={trackedKpis}
          canEdit={canConfigure}
          surface={surface}
          companyId={companyId}
          fiscalYearEnd={fiscalYearEnd}
          onEdit={openEditModal}
          onRecordActual={setActualKpi}
          onDelete={handleDelete}
          onAddTarget={canConfigure ? openAddTargetModal : undefined}
          onStatusChange={canConfigure ? handleStatusChange : undefined}
          isDeleting={deleteKpi.isPending}
          isStatusUpdating={updateKpi.isPending}
          ogsmLinks={canConfigure && isOgsmEnabled()}
        />
      )}

      {canConfigure ? (
        <>
          <ScorecardKpiModal
            open={isModalOpen}
            onOpenChange={setIsModalOpen}
            companyId={companyId}
            fiscalYearEnd={fiscalYearEnd}
            kpi={editingKpi}
            trackedKpi={trackedKpiForTarget}
          />
          {actualKpi ? (
            <ScorecardActualModal
              open
              onOpenChange={(open) => {
                if (!open) setActualKpi(null)
              }}
              companyId={companyId}
              kpi={actualKpi}
            />
          ) : null}
        </>
      ) : null}
    </div>
  )
}
